import { ref } from 'vue'
import { EventEmitter } from 'events'

export default ({ app }) => {
  const intercom = new Intercom({
    appId: process.env.INTERCOM_KEY
  })

  const placeholder = (...args) => placeholder.c(args)
  placeholder.q = []
  placeholder.c = args => placeholder.q.push(args)
  window.Intercom = placeholder

  app.config.globalProperties.$intercom = intercom
}

function loadAsyncScript (appId, done) {
  const script = document.createElement('script')
  script.type = 'text/javascript'
  script.async = true
  script.src = `https://widget.intercom.io/widget/${appId}`
  script.onload = done

  const firstScript = document.getElementsByTagName('script')[0]
  firstScript.parentNode.insertBefore(script, firstScript)
}

function isValidString (type, value) {
  if (!value || value.length === 0) return false

  return value instanceof type ||
    (value !== null && value !== undefined && value.constructor === type)
}

class Intercom extends EventEmitter {
  constructor ({ appId } = {}) {
    super()

    if (!isValidString(String, appId)) return

    this.appId = appId
    this.defaultOptions = { app_id: appId }
    this.isBooted = ref(false)
    this.ready = ref(false)
    this.visible = ref(false)
    this.unreadCount = ref(0)

    this.load()
  }

  resetValues () {
    this.visible.value = false
    this.unreadCount.value = 0
  }

  // Load intercom script with defer
  load () {
    if (!window || !document) return

    const load = () => loadAsyncScript(this.appId, () => this.init())

    if (document.readyState === 'complete') loadAsyncScript(this.appId, () => this.init())
    else if (window.attachEvent) window.attachEvent('onload', load)
    else window.addEventListener('load', load, false)
  }

  // Init Intercom service
  init () {
    this.ready.value = true

    this.emit('ready')
  }

  // Boot Intercom service with user
  boot (options) {
    this.callIntercom('boot', Object.assign({}, this.defaultOptions, options))

    this.callIntercom('onHide', () => {
      this.visible.value = false
    })
    this.callIntercom('onShow', () => {
      this.visible.value = true
    })
    this.callIntercom('onUnreadCountChange', unreadCount => {
      this.unreadCount.value = unreadCount
    })

    this.isBooted.value = true
    this.resetValues()
  }

  callIntercom (...args) {
    if (!window.Intercom) return
    return window.Intercom(...args)
  }

  shutdown () {
    this.isBooted.value = false
    this.resetValues()
    this.callIntercom('shutdown')
  }

  update (...options) { this.callIntercom('update', ...options) }

  toggleDefaultLauncher (val) { this.callIntercom('update', { hide_default_launcher: val }) }

  show () { this.callIntercom('show') }

  hide () { this.callIntercom('hide') }

  showMessages () { this.callIntercom('showMessages') }

  showNewMessage (content) {
    this.callIntercom('showNewMessage', ...(isValidString(String, content) ? [content] : []))
  }

  trackEvent (name, ...metadata) { this.callIntercom('trackEvent', ...[name, ...metadata]) }

  startTour (id, ...metadata) { this.callIntercom('startTour', ...[id, ...metadata]) }

  showArticle (articleId) { this.callIntercom('showArticle', articleId) }

  startSurvey (surveyId) { this.callIntercom('startSurvey', surveyId) }

  getVisitorId () { this.callIntercom('getVisitorId') }
}
